/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  OverflowMenu,
  OverflowMenuOption,
  IconSet,
  List,
  ListItem,
} from '@govhhs/govhhs-design-system-react';
import { RouterLink as Link } from '@spm/core-ui';
import LanguageDropdownMessages from './LanguageDropdownMessages';
/* eslint-disable no-console */
/**
 * The LanguageDropdownComponent component handles the language dropdown content
 * containing the supported languages.
 *
 * If a single language is provided nothing is rendered.
 * If two languages are provided a link to the unselected language and text element for the selected element will be rendered.
 * If two or more languages are provided a list of the languages will be rendered in an Overflow menu.
 *
 * @param props The component properties.
 *
 * @namespace LanguageDropdown
 * @memberof features.LanguageDropdown/components
 */
const LanguageDropdownComponent = ({ locales, intl, onLanguageToggle, language, warn }) => {
  const numberOfLocales = locales.length > 0 ? locales.length : 0;

  /**
   * Click handler for the language selection elements
   *
   * @param {Object} e Click event from the selected language element
   * @param {string} locale Associated locale
   */
  function handleLanguageSelect(e, locale) {
    e.preventDefault();
    onLanguageToggle(locale);
     /**
     * Start - 2319
     * We are taking this entire folder from ootb components folder and just added here to solve county - 2319 issue.
     * The below line is added to reload the page when we change the language and show the correct language code
     * table related descriptions in TO-DO's list. 
     * 
     * */
     window.location.reload(true);
     // END - 2319
  }

  switch (numberOfLocales) {
    case 0: /* No locale selector required */
    case 1: {
      warn(
        `Warning: The Language Dropdown component won't be displayed when a single language is configured.`
      );
      return null;
    }
    case 2: {
      /* Generate short list of locales */
      const shortlistLanguageItems = locales.map(value => {
        const { locale, displayName } = value;
        return language === locale ? (
          <ListItem key={`dropdown-language-${locale}`}>
            <p className="wds-u-type--meta" lang={locale}>
              {displayName}
            </p>
          </ListItem>
        ) : (
          <ListItem key={`dropdown-language-${locale}`}>
            {/* eslint-disable no-restricted-syntax */}
            {/*  eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              hrefLang={locale}
              lang={locale}
              onClick={e => handleLanguageSelect(e, locale)}
              to="#"
            >
              {displayName}
            </Link>
            {/* eslint-enable no-restricted-syntax */}
          </ListItem>
        );
      });

      return (
        <List data-testid="footer-language-selector" linear>
          {shortlistLanguageItems}
        </List>
      );
    }
    default: {
      const languageItems = locales.map(value => {
        const { locale, displayName } = value;

        return (
          <OverflowMenuOption key={`dropdown-language-${locale}`}>
            {/* eslint-disable no-restricted-syntax */}
            {/*  eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              hrefLang={locale}
              lang={locale}
              onClick={e => handleLanguageSelect(e, locale)}
              to="#"
            >
              {displayName}
            </Link>
            {/* eslint-enable no-restricted-syntax */}
          </OverflowMenuOption>
        );
      });

      return (
        <OverflowMenu
          ariaLabel={intl.formatMessage(LanguageDropdownMessages.dropdownLabel)}
          data-testid="footer-language-selector"
          isNav={{
            currentItemLabel: intl.formatMessage(LanguageDropdownMessages.currentItemLabel),
          }}
          title={
            <>
              <IconSet className="wds-u-mr--x-small" decorative name="translate" />
              {intl.formatMessage(LanguageDropdownMessages.dropdownLabel)}
            </>
          }
        >
          {languageItems}
        </OverflowMenu>
      );
    }
  }
};

LanguageDropdownComponent.defaultProps = {
  language: 'en',
  warn: console.warn,
};

LanguageDropdownComponent.propTypes = {
  intl: PropTypes.object.isRequired,
  language: PropTypes.string,
  locales: PropTypes.array.isRequired,
  onLanguageToggle: PropTypes.func.isRequired,
  warn: PropTypes.func,
};

export default injectIntl(LanguageDropdownComponent);
